// The Statistics window on the Players page: how many players there are, how many are
// here, how strong and how old the field is and where it comes from — for the whole
// tournament or for one group.
//
// The counting lives in js/utils/player-stats.js; this file chooses the players, draws
// the result and wires the button.

import { state, getActiveSettings } from '../state.js';
import { escapeHtml } from '../utils/formatting.js';
import { fieldStats } from '../utils/player-stats.js';
import { tournamentYear } from './prizes.js';
import { isNsfRequired, nsfSummary, nsfResultFromRegistration, nsfResultFromSearch } from '../utils/nsf-membership.js';
import {
    nsfCheckState, nsfResultForPlayer, nsfYearForGroup, nsfPickedYear, refreshNsfCheck, onNsfCheckChange
} from '../services/nsf-membership.js';

const modal = document.getElementById('player-stats-modal');
const body = document.getElementById('player-stats-body');
const scopeRow = document.getElementById('player-stats-scope-row');
const scopeSelect = document.getElementById('player-stats-scope');
const openButton = document.getElementById('btn-player-stats');

// The scope value for "every group together". Group ids are generated strings, so this
// cannot collide with one.
const ALL = '__all__';

// How many clubs the Clubs section lists before folding the rest into "and N more".
// A 200-player open has 60 clubs, and the useful question is which ones dominate.
const MAX_CLUBS = 10;

/**
 * Every group with the roster, rounds and settings the window needs.
 *
 * The active group is read from state.players and state.rounds, not from its group
 * object: that copy is only brought up to date on the next save (see selectGroup() in
 * js/state.js), and a player added a moment ago must not be missing from the count.
 * A tournament without groups is one nameless group.
 */
function groupsWithRosters() {
    const t = state.activeTournament;
    const groups = t && Array.isArray(t.groups) ? t.groups : [];
    if (groups.length === 0) {
        const settings = getActiveSettings();
        return [{
            id: null, name: '', players: state.players, rounds: state.rounds,
            roundsCount: parseInt(settings.roundsCount, 10) || 0,
            format: settings.format, categories: settings.categories,
            teams: (t && Array.isArray(t.teams)) ? t.teams : []
        }];
    }
    return groups.map(g => {
        const active = String(g.id) === String(state.activeGroupId);
        return {
            id: g.id,
            name: g.name || '',
            players: active ? state.players : (Array.isArray(g.players) ? g.players : []),
            rounds: active ? state.rounds : (Array.isArray(g.rounds) ? g.rounds : []),
            roundsCount: parseInt(g.roundsCount, 10) || 0,
            format: g.format,
            categories: g.categories,
            teams: Array.isArray(g.teams) ? g.teams : []
        };
    });
}

/** The groups the chosen scope covers. */
function groupsInScope(all) {
    const scope = scopeSelect ? scopeSelect.value : ALL;
    if (scope === ALL || all.length === 1) return all;
    const one = all.filter(g => String(g.id) === String(scope));
    return one.length ? one : all;
}

/**
 * The prize categories over several groups. Each group's players are tested against
 * that group's own categories, and categories with the same name are added together —
 * "Apply to all groups" gives every group the same list, and "U12: 14" across the
 * tournament is then exactly what an organizer buying medals wants to know.
 */
function mergedCategories(groups, year) {
    const byName = new Map();
    groups.forEach(g => {
        fieldStats(g.players, { year, categories: g.categories }).categories.forEach(c => {
            byName.set(c.name, (byName.get(c.name) || 0) + c.count);
        });
    });
    return [...byName].map(([name, count]) => ({ name, count }));
}

function pct(count, total) {
    return total > 0 ? Math.round((count / total) * 100) : 0;
}

function pctText(count, total) {
    return tr('{n}%', { n: pct(count, total) });
}

function label(template) {
    return tr(template.key, template.params);
}

// ----- drawing --------------------------------------------------------------

function tile(title, value, sub) {
    return `<div class="pstats-tile">
        <div class="pstats-tile-label">${escapeHtml(title)}</div>
        <div class="pstats-tile-value">${escapeHtml(value)}</div>
        ${sub ? `<div class="pstats-tile-sub">${escapeHtml(sub)}</div>` : ''}
    </div>`;
}

/**
 * Present against absent as one bar split in two, with a legend: two series, so the
 * legend is what tells them apart, and the counts sit beside it in text colour.
 */
function presenceBar(stats) {
    if (stats.total === 0) return '';
    return `<div class="pstats-presence">
        <div class="pstats-split" role="img"
             aria-label="${escapeHtml(tr('{present} present, {absent} absent', { present: stats.present, absent: stats.absent }))}">
            ${stats.present ? `<span class="pstats-split-present" style="flex-grow: ${stats.present};"></span>` : ''}
            ${stats.absent ? `<span class="pstats-split-absent" style="flex-grow: ${stats.absent};"></span>` : ''}
        </div>
        <div class="pstats-legend">
            <span><i class="pstats-swatch pstats-swatch-present"></i>${escapeHtml(tr('Present'))} <strong>${stats.present}</strong> <span class="pstats-muted">${escapeHtml(pctText(stats.present, stats.total))}</span></span>
            <span><i class="pstats-swatch pstats-swatch-absent"></i>${escapeHtml(tr('Absent'))} <strong>${stats.absent}</strong> <span class="pstats-muted">${escapeHtml(pctText(stats.absent, stats.total))}</span></span>
        </div>
    </div>`;
}

/**
 * One section of horizontal bars. Every row carries its count and its share of the
 * field as text, so the bar is never the only way to read a value — the list doubles
 * as its own table. Bars are scaled to the section's largest row, so the shape of a
 * small field is still visible.
 *
 * @param rows  [{ name, count, muted? }] — `muted` for the rows that sit outside the
 *              scale (unrated, unknown age, no club), drawn apart in grey.
 */
function barList(rows, total) {
    const max = Math.max(1, ...rows.map(r => r.count));
    return `<div class="pstats-bars">${rows.map(r => {
        const width = r.count > 0 ? Math.max(1.5, (r.count / max) * 100) : 0;
        const tip = tr('{name}: {count} players ({pct})', { name: r.name, count: r.count, pct: pctText(r.count, total) });
        return `<div class="pstats-row${r.muted ? ' pstats-row-muted' : ''}" title="${escapeHtml(tip)}">
            <span class="pstats-row-label">${escapeHtml(r.name)}</span>
            <span class="pstats-bar"><span class="pstats-bar-fill" style="width: ${width}%;"></span></span>
            <span class="pstats-row-value">${r.count} <span class="pstats-muted">${escapeHtml(pctText(r.count, total))}</span></span>
        </div>`;
    }).join('')}</div>`;
}

function section(title, summary, content) {
    return `<section class="pstats-section">
        <h3>${escapeHtml(title)}</h3>
        ${summary ? `<p class="pstats-summary">${summary}</p>` : ''}
        ${content}
    </section>`;
}

function summaryPart(name, value) {
    return `<span>${escapeHtml(name)} <strong>${escapeHtml(value)}</strong></span>`;
}

function groupsTable(groups, year) {
    const rows = groups.map(g => {
        const s = fieldStats(g.players, { year });
        const rounds = g.roundsCount
            ? tr('{played} of {total}', { played: g.rounds.length, total: g.roundsCount })
            : String(g.rounds.length);
        return `<tr class="pstats-group-row" data-group="${escapeHtml(g.id)}" tabindex="0"
                    title="${escapeHtml(tr('Show statistics for {group}', { group: g.name }))}">
            <td><div class="tbl-title">${escapeHtml(g.name || tr('Group'))}</div></td>
            <td class="pstats-num">${s.total}</td>
            <td class="pstats-num">${s.present}</td>
            <td class="pstats-num">${s.absent}</td>
            <td class="pstats-num">${s.rating.average === null ? '–' : s.rating.average}</td>
            <td class="pstats-num">${escapeHtml(rounds)}</td>
        </tr>`;
    }).join('');
    return section(tr('Groups'), '', `<div class="table-responsive">
        <table class="data-table compact-table pstats-groups">
            <thead><tr>
                <th>${escapeHtml(tr('Group'))}</th>
                <th class="pstats-num">${escapeHtml(tr('Players'))}</th>
                <th class="pstats-num">${escapeHtml(tr('Present'))}</th>
                <th class="pstats-num">${escapeHtml(tr('Absent'))}</th>
                <th class="pstats-num">${escapeHtml(tr('Average rating'))}</th>
                <th class="pstats-num">${escapeHtml(tr('Rounds'))}</th>
            </tr></thead>
            <tbody>${rows}</tbody>
        </table>
    </div>`);
}

function ratingSection(s) {
    const r = s.rating;
    if (r.rated === 0) {
        return section(tr('Rating'), '', `<p class="pstats-empty">${escapeHtml(tr('No player in this field has a rating.'))}</p>`);
    }
    const summary = [
        summaryPart(tr('Average'), r.average),
        summaryPart(tr('Median'), r.median),
        summaryPart(tr('Highest'), r.highest),
        summaryPart(tr('Lowest'), r.lowest)
    ].join('');
    const rows = r.bands.map(b => ({ name: label(b.label), count: b.count }));
    if (r.unrated) rows.push({ name: tr('Unrated'), count: r.unrated, muted: true });
    return section(tr('Rating'), summary, barList(rows, s.total));
}

function ageSection(s) {
    const a = s.age;
    if (a.known === 0) {
        return section(tr('Age'), '', `<p class="pstats-empty">${escapeHtml(tr('No birth years have been entered.'))}</p>`);
    }
    const summary = [
        summaryPart(tr('Average'), a.average),
        summaryPart(tr('Youngest'), a.youngest),
        summaryPart(tr('Oldest'), a.oldest)
    ].join('');
    const rows = a.bands.map(b => ({ name: label(b.label), count: b.count }));
    if (a.unknown) rows.push({ name: tr('Unknown'), count: a.unknown, muted: true });
    return section(tr('Age'), summary, barList(rows, s.total)
        + `<p class="pstats-note">${escapeHtml(tr('Age reached during {year}, the way the prize categories count it.', { year: s.year }))}</p>`);
}

function clubsSection(s) {
    const c = s.clubs;
    if (c.rows.length === 0) return '';
    const shown = c.rows.slice(0, MAX_CLUBS).map(r => ({ name: r.name, count: r.count }));
    const rest = c.rows.length - shown.length;
    if (rest > 0) {
        const restCount = c.rows.slice(MAX_CLUBS).reduce((n, r) => n + r.count, 0);
        shown.push({ name: tr('{n} other clubs', { n: rest }), count: restCount, muted: true });
    }
    if (c.missing) shown.push({ name: tr('No club'), count: c.missing, muted: true });
    return section(tr('Clubs'), '', barList(shown, s.total));
}

/** A section counting by a text field, shown only when there are at least two values. */
function countSection(title, counted, total, missingName) {
    if (counted.rows.length < 2) return '';
    const rows = counted.rows.map(r => ({ name: r.name, count: r.count }));
    if (counted.missing) rows.push({ name: missingName, count: counted.missing, muted: true });
    return section(title, '', barList(rows, total));
}

function titlesSection(s) {
    if (s.titles.rows.length === 0) return '';
    const rows = s.titles.rows.map(r => ({ name: r.name, count: r.count }));
    return section(tr('Titles'), '', barList(rows, s.total));
}

function sexSection(s) {
    if (s.sex.men + s.sex.women === 0) return '';
    const rows = [
        { name: tr('Men'), count: s.sex.men },
        { name: tr('Women'), count: s.sex.women }
    ];
    if (s.sex.unknown) rows.push({ name: tr('Not given'), count: s.sex.unknown, muted: true });
    return section(tr('Sex'), '', barList(rows, s.total));
}

function categoriesSection(categories, total) {
    if (categories.length === 0) return '';
    return section(tr('Prize Categories'), '', barList(categories, total)
        + `<p class="pstats-note">${escapeHtml(tr('A player can be in several categories.'))}</p>`);
}

/**
 * NSF membership over the groups in scope that require it, from the check the Players
 * page asked for (js/services/nsf-membership.js), or null when there is none to show:
 * no such group, a spectator, or nobody signed in.
 */
function nsfStats(groups) {
    const t = state.activeTournament;
    if (state.isSpectatorMode || !t) return null;
    const required = groups.filter(g => isNsfRequired(t, g.id));
    if (required.length === 0) return null;
    const check = nsfCheckState();
    if (check.status === 'off') return null;
    // Someone the check has not seen yet counts by what the player search showed when they
    // were picked from it, or by what was found when they registered.
    const results = required.flatMap(g => g.players.map(p =>
        nsfResultForPlayer(p)
        || nsfResultFromSearch(nsfPickedYear(p), nsfYearForGroup(g.id))
        || nsfResultFromRegistration(p, nsfYearForGroup(g.id))
        || null));
    return { check, summary: nsfSummary(results) };
}

function nsfSection(n) {
    const s = n.summary;
    const button = `<button type="button" class="btn-outline btn-small pstats-nsf-refresh"><i class="ri-refresh-line"></i> ${escapeHtml(tr('Check again'))}</button>`;
    if (!n.check.data) {
        const text = n.check.status === 'error' ? tr('The NSF check failed.') : tr('Checking NSF membership…');
        return section(tr('NSF Membership'), '', `<p class="pstats-empty">${escapeHtml(text)}</p>${button}`);
    }
    const rows = [{ name: tr('Paid members'), count: s.members }];
    if (s.fide) rows.push({ name: tr('Playing on a FIDE ID (NGP)'), count: s.fide });
    if (s.lapsed) rows.push({ name: tr('Paid for an earlier year'), count: s.lapsed });
    rows.push({ name: tr('To check'), count: s.toCheck });
    rows.push({ name: tr('Not found'), count: s.notFound });
    if (s.unchecked) rows.push({ name: tr('Not checked yet'), count: s.unchecked, muted: true });
    const reg = n.check.data.register || {};
    const published = reg.published ? trDate(String(reg.published).slice(0, 10)) : '';
    const notes = [
        tr('"Not found" is someone who has not paid this year, or whose name or year of birth is written differently. Click their badge on the player list.'),
        published ? tr('Checked against NSF\'s list of paid members, published {date}.', { date: published }) : ''
    ].filter(Boolean).map(note => `<p class="pstats-note">${escapeHtml(note)}</p>`).join('');
    return section(tr('NSF Membership'), '', barList(rows, s.total) + notes + button);
}

export function renderPlayerStats() {
    if (!body) return;

    const all = groupsWithRosters();
    const multi = all.length > 1;

    if (scopeRow) scopeRow.style.display = multi ? '' : 'none';
    if (scopeSelect && multi) {
        const prev = scopeSelect.value || ALL;
        scopeSelect.innerHTML = `<option value="${ALL}">${escapeHtml(tr('All groups'))}</option>`
            + all.map(g => `<option value="${escapeHtml(g.id)}">${escapeHtml(g.name || tr('Group'))}</option>`).join('');
        scopeSelect.value = all.some(g => String(g.id) === prev) ? prev : ALL;
    }

    const groups = groupsInScope(all);
    const year = tournamentYear();
    const players = groups.flatMap(g => g.players);

    if (players.length === 0) {
        body.innerHTML = `<p class="pstats-empty" style="padding: 2rem 0; text-align: center;">${escapeHtml(tr('There are no players yet.'))}</p>`;
        return;
    }

    const s = { ...fieldStats(players, { year }), year };
    const categories = mergedCategories(groups, year);

    const tiles = [
        tile(tr('Players'), String(s.total),
            s.registeredOnline ? tr('{n} registered online', { n: s.registeredOnline }) : ''),
        tile(tr('Present'), String(s.present), tr('{pct} of the field', { pct: pctText(s.present, s.total) })),
        tile(tr('Absent'), String(s.absent), tr('{pct} of the field', { pct: pctText(s.absent, s.total) }))
    ];
    if (groups.length > 1) tiles.push(tile(tr('Groups'), String(groups.length), ''));
    const teamGroups = groups.filter(g => g.format === 'team');
    if (teamGroups.length) {
        tiles.push(tile(tr('Teams'), String(teamGroups.reduce((n, g) => n + g.teams.length, 0)), ''));
    }
    tiles.push(
        tile(tr('Average rating'), s.rating.average === null ? '–' : String(s.rating.average),
            tr('{rated} rated, {unrated} unrated', { rated: s.rating.rated, unrated: s.rating.unrated })),
        tile(tr('Clubs'), String(s.clubs.rows.length),
            s.clubs.missing ? tr('{n} without a club', { n: s.clubs.missing }) : '')
    );
    const nsf = nsfStats(groups);
    if (nsf && nsf.check.data) {
        tiles.push(tile(tr('NSF members'), String(nsf.summary.members),
            tr('{check} to check, {missing} not found', { check: nsf.summary.toCheck, missing: nsf.summary.notFound })));
    }

    const sections = [
        nsf ? nsfSection(nsf) : '',
        ratingSection(s),
        ageSection(s),
        clubsSection(s),
        countSection(tr('Federations'), s.federations, s.total, tr('Not given')),
        titlesSection(s),
        sexSection(s),
        countSection(tr('Class'), s.classes, s.total, tr('No class')),
        categoriesSection(categories, s.total)
    ].filter(Boolean);

    body.innerHTML = `
        <div class="pstats-tiles">${tiles.join('')}</div>
        ${presenceBar(s)}
        ${groups.length > 1 ? groupsTable(groups, year) : ''}
        <div class="pstats-grid">${sections.join('')}</div>`;

    body.querySelectorAll('.pstats-nsf-refresh').forEach(btn => {
        btn.addEventListener('click', () => refreshNsfCheck({ force: true, immediate: true }));
    });

    // A group's row opens that group's statistics — the table is the overview, and the
    // natural next question is "and in this one?".
    body.querySelectorAll('.pstats-group-row').forEach(row => {
        const open = () => {
            if (!scopeSelect) return;
            scopeSelect.value = row.dataset.group;
            renderPlayerStats();
        };
        row.addEventListener('click', open);
        row.addEventListener('keydown', e => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
        });
    });
}

/**
 * Redraw the window if it is open. Called from renderPlayerList(), which runs whenever
 * the roster changes — a player marked present, or one arriving through the
 * registration form while the window is up, should change the numbers on screen.
 */
export function refreshPlayerStatsIfOpen() {
    if (modal && modal.classList.contains('active')) renderPlayerStats();
}

export function setupPlayerStats() {
    // An NSF check answering changes the membership numbers of an open window.
    onNsfCheckChange(refreshPlayerStatsIfOpen);
    if (openButton) {
        openButton.addEventListener('click', () => {
            renderPlayerStats();
            if (!modal) return;
            // Open at the headline numbers, not wherever the last visit was scrolled to.
            const scroller = modal.querySelector('.modal-body');
            if (scroller) scroller.scrollTop = 0;
            modal.classList.add('active');
        });
    }
    if (scopeSelect) scopeSelect.addEventListener('change', renderPlayerStats);
}
